import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { Comment } from '../../types/comment';

interface CommentState {
  items: Comment[];
  currentPostComments: Record<string, Comment[]>; // postId -> comments[]
  loading: boolean;
  error: string | null;
}

const initialState: CommentState = {
  items: [],
  currentPostComments: {},
  loading: false,
  error: null,
};

const commentSlice = createSlice({
  name: 'comments',
  initialState,
  reducers: {
    setComments: (state, action: PayloadAction<{ postId: string; comments: Comment[] }>) => {
      const { postId, comments } = action.payload;
      state.currentPostComments[postId] = comments;
    },
    addComment: (state, action: PayloadAction<Comment>) => {
      const comment = action.payload;
      state.items.push(comment);
      if (!state.currentPostComments[comment.postId]) {
        state.currentPostComments[comment.postId] = [];
      }
      state.currentPostComments[comment.postId].push(comment);
    },
    updateComment: (state, action: PayloadAction<Comment>) => {
      const updatedComment = action.payload;
      const index = state.items.findIndex((c) => c.id === updatedComment.id);
      if (index !== -1) {
        state.items[index] = updatedComment;
      }
      const postComments = state.currentPostComments[updatedComment.postId];
      if (postComments) {
        const commentIndex = postComments.findIndex((c) => c.id === updatedComment.id);
        if (commentIndex !== -1) {
          postComments[commentIndex] = updatedComment;
        }
      }
    },
    removeComment: (state, action: PayloadAction<{ commentId: string; postId: string }>) => {
      const { commentId, postId } = action.payload;
      state.items = state.items.filter((c) => c.id !== commentId);
      const postComments = state.currentPostComments[postId];
      if (postComments) {
        state.currentPostComments[postId] = postComments.filter((c) => c.id !== commentId);
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setComments,
  addComment,
  updateComment,
  removeComment,
  setLoading,
  setError,
} = commentSlice.actions;

export default commentSlice.reducer;

